/**
 * 연락처 추가/수정 다이얼로그.
 */
import React, { useEffect, useState } from "react";
import { Box, MenuItem, Stack, TextField, Typography } from "@mui/material";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";

import { mailApi } from "../../api/mailApi";
import { notify }  from "../../../../util/toast";
import FormModal   from "../../../../component/common/FormModal";
import ActionButton from "../../../../component/common/ActionButton";

const EMAIL_RE = /^[^\s<>@,;]+@[^\s<>@,;.]+(\.[^\s<>@,;.]+)+$/;

// 팀(우리 부서 공유)은 2026-08-08 폐지 — 주소록은 '나만'과 '회사가 함께' 둘로 갈린다.
// (배경: ContactScope 주석)
const SCOPE_OPTIONS = [
    { value: "PERSONAL", label: "개인 (나만 봄)" },
    { value: "PUBLIC",   label: "공용 (전 직원)" },
];

function SectionHeader({ title, subtitle }) {
    return (
        <Box sx={{ mb: 1.2, pb: 0.6, borderBottom: "1px solid #eef2f6" }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#0f172a", letterSpacing: 0.2 }}>
                {title}
            </Typography>
            {subtitle && (
                <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.2 }}>
                    {subtitle}
                </Typography>
            )}
        </Box>
    );
}

function formatPhone(raw) {
    if (raw == null) return "";
    const digits = raw.replace(/[^\d]/g, "");
    if (digits.length === 0) return "";

    if (digits.startsWith("010") || /^01[16789]/.test(digits)) {
        if (digits.length <= 3)  return digits;
        if (digits.length <= 7)  return `${digits.slice(0, 3)}-${digits.slice(3)}`;
        if (digits.length <= 11) return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
        return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
    }
    if (digits.startsWith("02")) {
        if (digits.length <= 2)  return digits;
        if (digits.length <= 5)  return `${digits.slice(0, 2)}-${digits.slice(2)}`;
        if (digits.length <= 9)  return `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`;
        return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6, 10)}`;
    }
    if (/^0[3-6]/.test(digits)) {
        if (digits.length <= 3)  return digits;
        if (digits.length <= 6)  return `${digits.slice(0, 3)}-${digits.slice(3)}`;
        if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
        return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
    }
    if (/^(15|16|18)/.test(digits) && digits.length <= 8) {
        return digits.length <= 4 ? digits : `${digits.slice(0, 4)}-${digits.slice(4, 8)}`;
    }
    if (digits.length <= 4) return digits;
    if (digits.length <= 8) return `${digits.slice(0, digits.length - 4)}-${digits.slice(-4)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, digits.length - 4)}-${digits.slice(-4)}`;
}

/**
 * @param readOnly     조회 전용. 내가 등록하지 않은 연락처는 <b>보이되 못 고친다</b> —
 *   행을 눌렀을 때 아무 반응이 없으면 "고장난 건가?" 로 읽히므로, 모달은 열되 입력만 잠근다.
 *   잠글 때 {@code disabled} 가 아니라 {@code readOnly} 를 쓰는 이유: disabled 는 글자까지 회색으로
 *   흐려서 정작 '조회' 가 목적인 화면에서 값이 잘 안 읽힌다. readOnly 는 색을 그대로 두고 입력만 막는다.
 * @param onComposeMail 이 연락처 주소로 메일 작성을 시작하는 콜백. 넘기면 푸터에 버튼이 생긴다.
 *   주소를 눈으로 읽고 다시 작성 화면에서 타이핑하는 왕복을 없애기 위한 것.
 */
export default function ContactEditDialog({ open, initial, onClose, onSaved, readOnly = false, onComposeMail }) {
    const [form, setForm] = useState({
        name: "", email: "", phone: "", companyPhone: "", company: "", position: "", memo: "", scope: "PERSONAL",
    });
    const [emailTouched, setEmailTouched] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm({
            name:         initial?.name     || "",
            email:        initial?.email    || "",
            phone:        formatPhone(initial?.phone || ""),
            companyPhone: formatPhone(initial?.companyPhone || ""),
            company:      initial?.company  || "",
            position:     initial?.position || "",
            memo:         initial?.memo     || "",
            scope:        initial?.scope    || "PERSONAL",
        });
        setEmailTouched(false);
    }, [open, initial]);

    const isEdit = Boolean(initial?.id);

    const handle = (k) => (e) => {
        const v = e.target.value;
        setForm(prev => ({
            ...prev,
            [k]: (k === "phone" || k === "companyPhone") ? formatPhone(v) : v,
        }));
    };

    const emailValid = !form.email.trim() ? false : EMAIL_RE.test(form.email.trim());
    const emailError = emailTouched && !emailValid;

    const handleSave = async () => {
        setEmailTouched(true);
        if (!emailValid) {
            notify("이메일 형식을 확인해주세요", "warning");
            return;
        }
        setSaving(true);
        try {
            if (isEdit) {
                await mailApi.updateContact(initial.id, form);
            } else {
                await mailApi.createContact(form);
            }
            notify(isEdit ? "연락처를 수정했습니다" : "연락처를 추가했습니다", "success");
            onSaved?.();
        } catch (e) {
            notify(e?.response?.data?.message || "저장 실패", "error");
        } finally { setSaving(false); }
    };

    return (
        <FormModal
            open={open}
            onClose={onClose}
            title={readOnly ? "연락처 보기" : isEdit ? "연락처 수정" : "새 연락처"}
            subtitle={readOnly
                ? "내가 등록한 연락처가 아니라 조회만 가능합니다."
                : "이메일은 필수 · 나머지는 선택"}
            maxWidth="sm"
            // onSave 를 넘기지 않으면 FormModal 이 저장 버튼 자체를 그리지 않는다 —
            // 눌러도 안 되는 버튼을 보여주는 것보다 없는 편이 오해가 없다.
            onSave={readOnly ? undefined : handleSave}
            cancelLabel={readOnly ? "닫기" : "취소"}
            saving={saving}
            // 저장된 연락처일 때만 — 아직 저장 전인 '새 연락처' 폼에서는 보낼 주소가 확정되지 않았다.
            footerExtras={(onComposeMail && isEdit && form.email.trim()) ? (
                <ActionButton
                    variant="secondary"
                    startIcon={<MailOutlineRoundedIcon sx={{ fontSize: 16 }} />}
                    onClick={() => onComposeMail({ name: form.name, email: form.email.trim() })}
                    label="이 주소로 메일 작성"
                    tooltip={`${form.email.trim()} 로 새 메일을 씁니다`}
                />
            ) : null}
        >
            <Stack spacing={2.5}>
                <Box>
                    <SectionHeader title="공유 범위" subtitle="개인은 나만, 공용은 전 직원이 봅니다." />
                    <TextField
                        select fullWidth size="small" label="공유 범위"
                        value={form.scope} onChange={handle("scope")}
                        disabled={isEdit && !readOnly}
                        SelectProps={{ readOnly }}
                        InputLabelProps={{ shrink: true }}
                        helperText={readOnly ? " " : isEdit ? "수정 시 공유 범위는 변경할 수 없습니다." : " "}
                    >
                        {/* 예전엔 비관리자에게 PUBLIC 항목을 걸러냈다. 서버가 공용 등록을 전 직원으로
                            열면서(2026-08-07) 걷어낸다 — 남겨두면 공용 탭에서 '새 연락처' 를 눌렀을 때
                            form.scope 는 PUBLIC 인데 고를 항목이 없어 선택칸이 빈 채로 뜬다. */}
                        {SCOPE_OPTIONS.map(o => (
                            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                    </TextField>
                </Box>

                <Box>
                    <SectionHeader title="기본 정보" subtitle="이름과 이메일 — 자동완성·정렬에 사용됩니다." />
                    <Stack spacing={1.5}>
                        <TextField
                            fullWidth label="이름" size="small"
                            value={form.name} onChange={handle("name")}
                            InputProps={{ readOnly }}
                            InputLabelProps={{ shrink: true }}
                            placeholder="홍길동"
                        />
                        <TextField
                            fullWidth required label="이메일" size="small" type="email"
                            value={form.email}
                            onChange={handle("email")}
                            onBlur={() => setEmailTouched(true)}
                            error={emailError}
                            helperText={emailError ? "이메일 형식이 올바르지 않습니다 (예: user@example.com)" : " "}
                            InputProps={{ readOnly }}
                            InputLabelProps={{ shrink: true }}
                            placeholder="user@example.com"
                            inputProps={{ pattern: "[^\\s<>@,;]+@[^\\s<>@,;.]+(\\.[^\\s<>@,;.]+)+" }}
                        />
                    </Stack>
                </Box>

                <Box>
                    <SectionHeader title="소속 정보" subtitle="회사·직책은 자동완성 검색에 함께 노출됩니다." />
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                        <TextField fullWidth label="회사" size="small" value={form.company}
                            onChange={handle("company")} InputProps={{ readOnly }} InputLabelProps={{ shrink: true }}
                            placeholder="(주)온앤미" />
                        <TextField fullWidth label="직책" size="small" value={form.position}
                            onChange={handle("position")} InputProps={{ readOnly }} InputLabelProps={{ shrink: true }}
                            placeholder="대리 / 팀장 등" />
                    </Box>
                </Box>

                <Box>
                    <SectionHeader title="연락 정보" subtitle="숫자만 입력하면 자동으로 하이픈이 들어갑니다." />
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                        <TextField
                            fullWidth label="휴대폰" size="small" type="tel"
                            value={form.phone} onChange={handle("phone")}
                            InputProps={{ readOnly }}
                            InputLabelProps={{ shrink: true }}
                            placeholder="010-0000-0000"
                            inputProps={{ inputMode: "numeric", maxLength: 13 }}
                        />
                        <TextField
                            fullWidth label="회사 번호" size="small" type="tel"
                            value={form.companyPhone} onChange={handle("companyPhone")}
                            InputProps={{ readOnly }}
                            InputLabelProps={{ shrink: true }}
                            placeholder="02-0000-0000"
                            inputProps={{ inputMode: "numeric", maxLength: 13 }}
                        />
                    </Box>
                </Box>

                <Box>
                    <SectionHeader title="기타" subtitle="필요 시 메모를 남겨두세요. (옵션)" />
                    <TextField fullWidth label="메모" size="small" multiline minRows={3}
                        value={form.memo} onChange={handle("memo")}
                        InputProps={{ readOnly }} InputLabelProps={{ shrink: true }}
                        placeholder="처음 만난 자리·관계·자주 보내는 메일 등" />
                </Box>
            </Stack>
        </FormModal>
    );
}
